import React from 'react';
import { Box, Flex, Text, IconButton, Stack, HStack, Icon } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { FaEye, FaMapMarkerAlt } from 'react-icons/fa';
import { FiAlertCircle, FiAlertTriangle } from 'react-icons/fi';
import { EVENT_TYPE_COLORS, getEventPriority } from '../../../../utils/clinicalEvents';
import { calculateTimePassed } from '../../../../utils/CalculateTimePassed';
import { convertDateAndTimeNow } from '../../../../utils/ConvertDateNow';

export const translateEventName = (name, t) => {
	switch (name) {
		case 'SHOCK':
			return t('Pulsaciones anormales');
		case 'LOW_BATTERY':
			return t('Low battery');
		case 'FALLDOWN':
			return t('Fall');
		case 'GEO_FENCE':
			return t('Geofence notice');
		case 'MEDICINE_REMINDER':
			return t('Medication reminder');
		case 'OXYGEN':
			return t('Low oxygen saturation');
		case 'BLOOD_PRESSURE':
			return t('Blood pressure alert');
		case 'TEMPERATURE':
			return t('Temperature Alert');
		case 'POWER_OFF':
			return t('Off');
		case 'WATCH_REMOVED':
			return t('Detached');
		case 'WATCH_CONNECTED':
			return t('Connected');
		case 'WATCH_DISCONNECTED':
			return t('Offline');
		default:
			return t(name);
	}
};

export const getEventMainValue = (ev, t) => {
	switch (ev.type) {
		case 'SHOCK':
			return ev.value?.heartRate ? `${ev.value.heartRate} BPM` : '';
		case 'OXYGEN':
			return ev.value?.oxygen ? `${ev.value.oxygen} %` : '';
		case 'BLOOD_PRESSURE':
			return ev.value?.bloodPressure
				? `${ev.value.bloodPressure.SYSTOLIC}/${ev.value.bloodPressure.DIASTOLIC} mmHg`
				: '';
		case 'GEO_FENCE':
			if (ev.value?.distance) {
				const userLocale =
					(ev.Patient &&
						ev.Patient.User &&
						ev.Patient.User.language) ||
					'es-ES';
				const n = Number(ev.value.distance);
				const txt = !isNaN(n)
					? n.toLocaleString(userLocale, {
							minimumFractionDigits: 0,
							maximumFractionDigits: 0,
							useGrouping: true,
						})
					: ev.value.distance;
				return `${txt} ${t('Meters outside')}`;
			}
			return '';
		case 'MEDICINE_REMINDER':
			return ev.value?.reminderText || '';
		case 'TEMPERATURE':
			return ev.value?.temperature
				? `${ev.value.temperature} ºC`
				: '';
		case 'LOW_BATTERY':
			return ev.value?.powerPercentage
				? `${ev.value.powerPercentage} %`
				: '';
		default:
			return '';
	}
};

export const DashboardCardEventsList: React.FC<any> = React.memo(({
	events = [],
	fullPatient,
	onOpenGeoFence,
	onDismissEvent,
}) => {
	const { t } = useTranslation();

	if (!events || events.length === 0) return null;

	return (
		<Stack mb={2}>
			{events.map(ev => (
				<Box
					key={ev.id}
					bg={EVENT_TYPE_COLORS[ev.type]}
					p={4}
					borderRadius='2xl'
					minH='100px'
					display='flex'
					flexDirection='column'
					justifyContent='space-between'
				>
					<Flex
						align='center'
						mb={1}
						w='100%'
						position='relative'
						justify='space-between'
					>
						<HStack spacing={2}>
							{getEventPriority(ev.type) <= 4 ? (
								<HStack
									spacing={1}
									bg='blackAlpha.500'
									color='white'
									px={2}
									py={0.5}
									borderRadius='full'
									boxShadow='xs'
								>
									<Icon as={FiAlertCircle} boxSize='12px' color='red.200' />
									<Text fontSize='10px' fontWeight='bold' textTransform='uppercase'>
										{t('Critical')}
									</Text>
								</HStack>
							) : getEventPriority(ev.type) <= 9 ? (
								<HStack
									spacing={1}
									bg='blackAlpha.400'
									color='white'
									px={2}
									py={0.5}
									borderRadius='full'
									boxShadow='xs'
								>
									<Icon as={FiAlertTriangle} boxSize='12px' color='yellow.200' />
									<Text fontSize='10px' fontWeight='bold' textTransform='uppercase'>
										{t('Warning')}
									</Text>
								</HStack>
							) : (
								<HStack
									spacing={1}
									bg='blackAlpha.300'
									color='white'
									px={2}
									py={0.5}
									borderRadius='full'
									boxShadow='xs'
								>
									<Text fontSize='10px' fontWeight='bold' textTransform='uppercase'>
										{t('Info')}
									</Text>
								</HStack>
							)}

							{ev.type === 'GEO_FENCE' && onOpenGeoFence && (
								<IconButton
									type='button'
									icon={<FaMapMarkerAlt />}
									size='sm'
									minW='44px'
									minH='44px'
									variant='ghost'
									colorScheme='whiteAlpha'
									aria-label='Ver geovalla'
									onClick={() => onOpenGeoFence(fullPatient)}
									_hover={{
										bg: 'whiteAlpha.300',
									}}
									_focusVisible={{
										outline: '3px solid #fff',
										outlineOffset: '2px',
									}}
									border='1px solid #fff'
									borderRadius='full'
									bg='rgba(255,255,255,0.18)'
									boxShadow='md'
								/>
							)}
						</HStack>
						<Box>
							{onDismissEvent && (
								<IconButton
									type='button'
									icon={<FaEye />}
									size='sm'
									minW='44px'
									minH='44px'
									variant='ghost'
									colorScheme='whiteAlpha'
									aria-label='marcar leído'
									onClick={() => onDismissEvent(ev.id)}
									_hover={{
										bg: 'whiteAlpha.300',
									}}
									_focusVisible={{
										outline: '3px solid #fff',
										outlineOffset: '2px',
									}}
									border='1px solid #fff'
									borderRadius='full'
									bg='rgba(255,255,255,0.18)'
									boxShadow='md'
								/>
							)}
						</Box>
					</Flex>

					<Box w='100%' mb={2}>
						<Text
							fontSize='2xl'
							fontWeight='bold'
							color='#fff'
							letterSpacing={1}
							textAlign='center'
							maxW='90%'
							mx='auto'
							overflow='hidden'
							textOverflow='ellipsis'
							whiteSpace='nowrap'
						>
							{translateEventName(ev.type, t)}
						</Text>
					</Box>

					<Text
						fontSize='xl'
						fontWeight='bold'
						color='#fff'
						textAlign='center'
						mb={2}
						maxW='90%'
						mx='auto'
						overflow='hidden'
						textOverflow='ellipsis'
						whiteSpace='nowrap'
					>
						{getEventMainValue(ev, t)}
					</Text>

					<Flex
						justify='space-between'
						align='center'
						mt={2}
					>
						<Text
							fontSize='sm'
							color='#fff'
						>
							{ev.createdAt
								? convertDateAndTimeNow(ev.createdAt)
								: ''}
						</Text>
						<Text
							fontSize='sm'
							color='#fff'
							opacity={0.9}
						>
							{ev.createdAt
								? t('hace') +
									' ' +
									calculateTimePassed(ev.createdAt)
								: ''}
						</Text>
					</Flex>
				</Box>
			))}
		</Stack>
	);
});
DashboardCardEventsList.displayName = 'DashboardCardEventsList';

export default DashboardCardEventsList;
